import { TrackManagerSeason } from "@/types/shared";
import { Badge, DropdownMenu, Flex, IconButton, Text } from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { EllipsisVertical, MapPin, Pencil, Calendar } from "lucide-react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

interface Props {
  seasons: TrackManagerSeason[];
  setSelectedRow: React.Dispatch<
    React.SetStateAction<TrackManagerSeason | null>
  >;
}

function formatDateRange(start: string | null, end: string | null) {
  if (!start && !end) return "—";
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return fmt(start);
  return fmt(end!);
}

export default function SeasonsTable({ seasons, setSelectedRow }: Props) {
  const [data, setData] = useState(() => [...seasons]);

  useEffect(() => {
    setData([...seasons]);
  }, [seasons]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<TrackManagerSeason>();

  const columns = useMemo<ColumnDef<TrackManagerSeason, any>[]>(
    () => [
      columnHelper.accessor("track_name", {
        header: "Track",
        meta: { filterVariant: "text" },
        cell: (props) => <Text>{props.row.original.track_name}</Text>,
      } as AccessorFnColumnDef<TrackManagerSeason, any>),
      columnHelper.accessor("name", {
        header: "Season",
        meta: { filterVariant: "text" },
        cell: (props) => <Text weight="medium">{props.row.original.name}</Text>,
      } as AccessorFnColumnDef<TrackManagerSeason, any>),
      columnHelper.accessor("start_date", {
        header: "Dates",
        cell: (props) =>
          formatDateRange(
            props.row.original.start_date,
            props.row.original.end_date,
          ),
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (props) => (
          <Badge size="2" variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "draft", title: "Draft" },
            { value: "published", title: "Published" },
          ],
        },
      } as AccessorFnColumnDef<TrackManagerSeason, any>),
      columnHelper.accessor("phase", {
        header: "Phase",
        cell: (props) => (
          <Badge size="2" variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "upcoming", title: "Upcoming" },
            { value: "active", title: "Active" },
            { value: "past", title: "Past" },
          ],
        },
      } as AccessorFnColumnDef<TrackManagerSeason, any>),
      columnHelper.accessor("event_count", {
        header: "Events",
        cell: (props) => props.getValue(),
        meta: { filterVariant: "number" },
      }),
      columnHelper.accessor("title_sponsor", {
        header: "Title Sponsor",
        cell: (props) => (
          <Text color={props.getValue() ? undefined : "gray"}>
            {props.getValue() || "—"}
          </Text>
        ),
        meta: { filterVariant: "text" },
      } as AccessorFnColumnDef<TrackManagerSeason, any>),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (props) => (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton
                variant="ghost"
                size="1"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVertical size="16px" />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/track_manager/seasons/${props.row.original.slug}/edit`;
                }}
              >
                <Pencil size="14px" />
                Edit Season
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/track_manager/seasons/${props.row.original.slug}/events`;
                }}
              >
                <Calendar size="14px" />
                Manage Events
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/tracks/${props.row.original.track_slug}/seasons/${props.row.original.slug}`;
                }}
              >
                <Calendar size="14px" />
                View Public Page
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/tracks/${props.row.original.track_slug}`;
                }}
              >
                <MapPin size="14px" />
                View Track
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(row) => setSelectedRow(row.original)}
    />
  );
}
